import { useEffect, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Box,
  Grid,
  Stack,
  styled,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import {
  DataGridPro,
  GridSortItem,
  type GridCallbackDetails,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';

import {
  ProviderCallOutcomeBC,
  ProviderCallOutcomeResponse,
} from '../../../../data/models/provider-call-outcome.model';
import type { Provider } from '../../../../data/models/provider.model';
import type { PagedList } from '../../../../data/models/paged-list.model';
import type {
  SyncedTripLeg,
  TripLeg,
} from '../../../../data/models/reservation.model';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';

import addressTransformUtils from '../../../../services/data-transform/addressTransformUtils';
import { phoneUtils } from '../../../../services/phoneUtils';
import tripLegUtils from '../../../../services/triplegUtils';
import { financialUtils } from '../../../../services/financialUtils';
import dateUtils from '../../../../services/DateUtils';

import {
  fetchProvidersCallOutcomesAsync,
  selectLoadingProviderCallOutcomes,
  selectProviderCallOutcomes,
} from '../../../../data/provider.slice';
import {
  selectCallOutcomeRejectReasons,
  selectCallOutcomes,
  selectTimeZones,
} from '../../../../data/lookup.slice';
import {
  fetchPassengerAsync,
  selectCurrentPassenger,
} from '../../../../data/passenger.slice';

import { CellDualValue } from '../../../tables/DataGridPro/CellDualValue';
import { CellStatusChip } from '../../../tables/DataGridPro/CellStatusChip';
import { SortModel } from '../../../../data/models/sort-model.model';

const PREFIX = 'ProviderSelectForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
  tripLegSummaryLabel: `${PREFIX}-tripLegSummaryLabel`,
  tripLegSummaryValue: `${PREFIX}-tripLegSummaryValue`,
  providerName: `${PREFIX}-providerName`,
  providerPhone: `${PREFIX}-providerPhone`,
  noAnswer: `${PREFIX}-noAnswer`,
  rejected: `${PREFIX}-rejected`,
  notOffered: `${PREFIX}-notOffered`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch',
  gap: 24,
  width: '100%',

  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: 24,
    width: '100%',
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },

  [`& .${classes.tripLegSummaryLabel}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.17px',
    paddingBottom: 0,
  },

  [`& .${classes.tripLegSummaryValue}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.17px',
    whiteSpace: 'pre-line',
  },

  [`& .${classes.providerName}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: '#000000',
  },

  [`& .${classes.providerPhone}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: 'rgba(1, 16, 18, 0.6)',
    marginTop: 0,
  },

  [`& .MuiDataGrid-iconButtonContainer`]: {
    width: 'auto',
  },
}));

const StyledFilterDiv = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    width: '100%',
    height: '48px',
    maxHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
}));

const StyledSearchContainer = styled('div')({
  minWidth: '327px',
  height: '100%',
  padding: '12px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  background: '#FFFFFF',
  borderRadius: '3px',
  boxShadow: '0px 2px 7px 0px #01101226',
  color: '#0000000F',
});

const StyledInput = styled('input')({
  width: '100%',
  border: 'none',
  outline: 'none',
  color: '#01101299',
  fontFamily: 'Source Sans Pro',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textAlign: 'left',
  textUnderlinePosition: 'from-font',
  textDecorationSkipInk: 'none',
});

const StyledSearchIconContainer = styled('div')({
  width: '24px',
  height: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ProviderStatus = ({
  provider,
  tripLegProviderCallOutcomes,
}: {
  provider: Provider;
  tripLegProviderCallOutcomes?: PagedList<ProviderCallOutcomeResponse>;
}) => {
  let label = 'Not Offered';
  let subLabel: string | null = null;
  let classNames = [classes.notOffered];
  let variant = 'primary';
  const callOutcomes = useAppSelector(selectCallOutcomes);
  const rejectedReasons = useAppSelector(selectCallOutcomeRejectReasons);
  if (tripLegProviderCallOutcomes?.items?.length) {
    const callOutcome = tripLegProviderCallOutcomes?.items?.find(
      (callOutcome) =>
        callOutcome?.transportationProviderID ===
        provider?.transportationProviderID,
    )?.lastTripLegProviderCallOutcome;
    if (callOutcome) {
      const callOutcomeBC = callOutcomes.find(
        (callOutcomeBC) =>
          callOutcomeBC.callOutcomeID === callOutcome.callOutcomeID,
      );
      if (callOutcomeBC) {
        subLabel = dateUtils.formatDateTime(
          DateTime.fromISO(callOutcome.createdOnUTC!),
        );
        switch (callOutcomeBC.callOutcomeBC) {
          case ProviderCallOutcomeBC.NoAnswer:
            label = callOutcomeBC.callOutcomeName;
            variant = 'warning';
            break;
          case ProviderCallOutcomeBC.Rejected:
            label = `${callOutcomeBC.callOutcomeName} - ${
              rejectedReasons.find(
                (reason) =>
                  reason.callOutcomeRejectReasonID ===
                  callOutcome.callOutcomeRejectReasonID,
              )?.callOutcomeRejectReasonName
            }`;

            variant = 'error';
            break;
        }
      }
    }
  }
  return <CellStatusChip variant={variant} label={label} subLabel={subLabel} />;
};

const DataPoint = ({ label, value }: { label: string; value?: string }) => {
  return (
    <Box>
      <Typography className={classes.tripLegSummaryLabel}>{label}</Typography>
      <Typography className={classes.tripLegSummaryValue}>{value}</Typography>
    </Box>
  );
};

interface ProviderSelectFormProps {
  tripLeg: SyncedTripLeg;
  tripLegsInReservation: TripLeg[];
  selectedProvider: Provider | null;
  setSelectedProvider: (provider: Provider | null) => void;
  close: () => void;
}

const ProviderSelectForm = ({
  tripLeg,
  tripLegsInReservation,
  selectedProvider,
  setSelectedProvider,
  close,
}: ProviderSelectFormProps) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchFilter = useDebounce(searchValue, 500);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const defaultSortItem: GridSortItem = {
    field: 'numberOfCallOutcome',
    sort: 'desc',
  };
  const [sortModel, setSortModel] = useState<GridSortModel>([defaultSortItem]);
  const sortItem = sortModel.length > 0 ? sortModel[0] : defaultSortItem;
  const mappedSortModel: SortModel = {
    column: sortItem.field,
    direction: sortItem.sort,
  };

  const loadingProviderCallOutcomes = useAppSelector(
    selectLoadingProviderCallOutcomes,
  );
  const providerCallOutcomes = useAppSelector(selectProviderCallOutcomes);
  const timezones = useAppSelector(selectTimeZones);
  const passengerData = useAppSelector(selectCurrentPassenger);

  const passengerName = `${tripLeg.passengerFirstName ?? ''} ${tripLeg.passengerLastName ?? ''}`;
  let passengerPhone = '';
  if (passengerData?.passengerPhones?.length) {
    passengerPhone =
      phoneUtils.getPassengerMobilePhone(passengerData.passengerPhones)
        ?.phoneNumber ||
      phoneUtils.getPassengerHomePhone(passengerData.passengerPhones)
        ?.phoneNumber ||
      '';
    passengerPhone = phoneUtils.formatPhoneString(passengerPhone, false, false);
  }
  const companionCount = tripLeg.companionCount ?? 0;
  const isMoreThanOnePassenger = companionCount > 0;
  const tripAndPassengerCountLabel = isMoreThanOnePassenger
    ? 'findProvider.tripAndPassengerCountPlural'
    : 'findProvider.tripAndPassengerCountSingular';
  const tripAndPassengerCount = t(tripAndPassengerCountLabel, {
    tripLegReferenceID: tripLeg.tripLegReferenceID,
    numPassengers: 1 + companionCount,
  });
  const memberData = `${passengerName}
  ${tripAndPassengerCount}
  ${passengerPhone}`;

  let pickupData = '';
  if (tripLeg) {
    let puTimeData = '';
    if (tripLeg.isWillCall) {
      puTimeData = t('findProvider.willCall');
    } else {
      const tz = timezones.find(
        (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
      );
      const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
        zone: tz?.ianaStandard,
      });
      const timeUntilPickup = m.toRelative({
        style: 'long',
        round: true,
      });
      puTimeData = `${m.toFormat('h:mm a')} (${timeUntilPickup})`;
    }

    const pickupAddress = addressTransformUtils.getDisplayAddress({
      addressLine1: tripLeg.pickUpAddressLine1,
      addressLine2: tripLeg.pickUpAddressLine2,
      city: tripLeg.pickUpCity,
      stateCode: tripLeg.pickUpStateCode,
      zipCode: tripLeg.pickUpZipCode,
    });

    pickupData = `${puTimeData}
      ${pickupAddress}`;
  }

  let dropoffData = '';
  if (tripLeg) {
    const dropoffAddress = addressTransformUtils.getDisplayAddress({
      addressLine1: tripLeg.dropOffAddressLine1,
      addressLine2: tripLeg.dropOffAddressLine2,
      city: tripLeg.dropOffCity,
      stateCode: tripLeg.dropOffStateCode,
      zipCode: tripLeg.dropOffZipCode,
    });
    dropoffData = `${dropoffAddress}`;
  }

  const tripLegDistance = tripLegUtils.formatDistance({
    distance: Number(tripLeg.estimatedDistanceMiles ?? 0),
    unitPlural: t('findProvider.miles'),
    unitSingular: t('findProvider.mile'),
  });

  const totalDistance = tripLegUtils.formatDistance({
    distance: tripLegsInReservation.reduce(
      (acc, leg) => acc + Number(leg?.estimatedDistance ?? 0),
      0,
    ),
    unitPlural: t('findProvider.miles'),
    unitSingular: t('findProvider.mile'),
  });

  const tripLegPrice = financialUtils.formatPrice(
    Number(tripLeg.payableAmount ?? 0),
  );

  const totalPrice = tripLegsInReservation.reduce(
    (acc, leg) => acc + Number(leg?.payableAmount ?? 0),
    0,
  );

  const columns: GridColDef[] = [
    {
      field: 'transportationProviderName',
      headerName: t('findProvider.provider') || '',
      minWidth: 345,
      renderCell: (params: GridCellParams) => {
        return (
          <CellDualValue
            primaryValue={params.row.transportationProviderName}
            secondaryValue={phoneUtils.formatPhoneString(
              params.row?.phoneNumbers?.[0]?.phoneNumber || '',
              false,
              true,
            )}
          />
        );
      },
    },
    {
      field: 'numberOfCallOutcome',
      headerName: t('common.status') || '',
      align: 'center',
      headerAlign: 'center',
      minWidth: 210,
      renderCell: (params: GridCellParams) => (
        <ProviderStatus
          provider={params.row}
          tripLegProviderCallOutcomes={providerCallOutcomes}
        />
      ),
    },
  ];

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleSelectProvider = (evt: any) => {
    const provider = evt.row;
    if (provider) {
      setSelectedProvider?.(provider);
    }
  };

  const handleClose = () => {
    setSearchValue('');
    setPage(1);
    setPageSize(10);
    setSortModel([defaultSortItem]);
    close();
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== providerCallOutcomes.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  useEffect(() => {
    setSearchValue(debouncedSearchFilter !== null ? debouncedSearchFilter : '');
  }, [debouncedSearchFilter]);

  useEffect(() => {
    dispatch(
      fetchProvidersCallOutcomesAsync({
        tripLegReferenceID: tripLeg.tripLegReferenceID!,
        sortModel: mappedSortModel,
        search: searchValue,
        page,
        pageSize,
        transportationProviderTier: '1,2,3',
      }),
    );
    dispatch(fetchPassengerAsync(tripLeg.passengerUUID!));
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      return;
    }
    dispatch(
      fetchProvidersCallOutcomesAsync({
        tripLegReferenceID: tripLeg.tripLegReferenceID!,
        sortModel: mappedSortModel,
        search: searchValue,
        page,
        pageSize,
        transportationProviderTier: '1,2,3',
      }),
    );
  }, [sortModel, searchValue, page, pageSize, selectedProvider]);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.content}>
        <Grid container>
          <Grid item xs={8}>
            <Grid container>
              <Grid item xs={3}>
                <DataPoint
                  label={t('findProvider.member')}
                  value={memberData}
                />
              </Grid>
              <Grid item xs={3}>
                <DataPoint
                  label={t('findProvider.pickup')}
                  value={pickupData}
                />
              </Grid>
              <Grid item xs={3}>
                <DataPoint
                  label={t('findProvider.dropoff')}
                  value={dropoffData}
                />
              </Grid>
              <Grid item xs={2}>
                <Stack spacing={1}>
                  <DataPoint
                    label={t('findProvider.distance')}
                    value={tripLegDistance}
                  />
                </Stack>
              </Grid>
              <Grid item xs={1} display="flex">
                <Divider orientation="vertical" />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <Grid container gap={1}>
              <Grid item xs={12}>
                <DataPoint
                  label={`${t('findProvider.reservation')} ${tripLeg.reservationReferenceID}`}
                  value={`Leg ${tripLeg.tripLegSequence || 1} of ${tripLegsInReservation.length} (${tripLeg.tripLegReferenceID})`}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className={classes.tripLegSummaryLabel}>
                  {t('findProvider.totalDistance')}
                </Typography>
                <Typography className={classes.tripLegSummaryValue}>
                  {totalDistance}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <StyledFilterDiv className={classes.container}>
          <StyledSearchContainer>
            <StyledSearchIconContainer>
              <SearchIcon sx={{ color: '#0110128A' }} />
            </StyledSearchIconContainer>
            <StyledInput
              placeholder={t('findProvider.searchPlaceholder')}
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
            />
          </StyledSearchContainer>

          {/* <StyledMenuButton onClick={handleTierSelectToggle}>
            {tierSelectOptions[selectedTier]}
            {tierSelectMenuOpen && (
              <ExpandLessIcon sx={{ color: '#0110128A' }} />
            )}
            {!tierSelectMenuOpen && (
              <ExpandMoreIcon sx={{ color: '#0110128A' }} />
            )}
          </StyledMenuButton>
          <StyledMenuList
            open={tierSelectMenuOpen}
            onClose={handleTierSelectToggle}
            anchorEl={tierSelectMenuAnchorElm}>
            {tierSelectOptions.map((option, idx) => (
              <MenuItem
                key={`tier-option-${option}`}
                onClick={(evt: any) => handleTierSelect(evt, idx)}>
                {option}
              </MenuItem>
            ))}
          </StyledMenuList> */}
        </StyledFilterDiv>

        <DataGridPro
          getRowId={(row: any) => row.transportationProviderID}
          columns={columns}
          rows={providerCallOutcomes.items}
          rowCount={providerCallOutcomes.itemCount}
          pagination={true}
          paginationMode="server"
          sortingMode="server"
          paginationModel={{
            page: providerCallOutcomes.currentPage - 1,
            pageSize,
          }}
          pageSizeOptions={[10, 20, 40, 50, 100]}
          onPaginationModelChange={paginationChange}
          autoHeight={true}
          onRowClick={handleSelectProvider}
          loading={loadingProviderCallOutcomes}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          disableRowSelectionOnClick={true}
          disableMultipleRowSelection={true}
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          sortingOrder={['asc', 'desc']}
          initialState={{
            sorting: {
              sortModel: [{ field: 'transportationProviderName', sort: 'asc' }],
            },
          }}
        />
      </Paper>
      <Box className={classes.actions}>
        <Button color="secondary" variant="contained" onClick={handleClose}>
          {t('common.close')}
        </Button>
      </Box>
    </StyledBox>
  );
};

export { ProviderSelectForm };
